// ---------------------------------------------------------------------------
// Which `rules:*` permission each Data Quality Rules action needs.
//
// Mirrors ztd_app/api/modules/dq_rules/router.py (`require_permission(...)` on
// each route), which is what actually decides. The UI gate only exists so a
// user sees WHY a control is unavailable instead of a bare 403:
//
//   run, nl      -> rules:execute   POST /execute-batch, /{key}/execute-direct, /parse-nl
//   create       -> rules:create    POST / (create), POST /customize (reuse a library rule)
//   update       -> rules:update    PUT /{key}, POST /{key}/submit
//   approve      -> rules:approve   POST /{key}/approve, /{key}/reject
//   retire       -> rules:delete    DELETE /{key} (a Data Steward's action)
//   adopt        -> rules:adopt_pack POST /packs/adopt (a Data Steward's action)
//
// Since DQ rule governance (2026-11-01) these come from the user's role ON THE
// PROJECT (DQ Analyst: read/create/update/execute; Data Steward: also
// approve/delete), which useRulesGates(projectId) reads from GET /access.
// Pure functions, no React: unit-tested directly.
// ---------------------------------------------------------------------------

export type RulesAction =
  | "run"
  | "nl"
  | "create"
  | "update"
  | "approve"
  | "retire"
  | "adopt";

export const RULES_ACTION_PERMISSION: Readonly<Record<RulesAction, string>> = {
  run: "rules:execute",
  nl: "rules:execute",
  create: "rules:create",
  update: "rules:update",
  approve: "rules:approve",
  retire: "rules:delete",
  adopt: "rules:adopt_pack",
};

/**
 * Whether `granted` satisfies `required`. Same rule as the backend's
 * `_permission_matches` (auth/dependencies.py): the `*` wildcard, an exact
 * codename, or the category wildcard (`rules:*` satisfies `rules:execute`).
 */
export function permissionGranted(
  granted: readonly string[] | null | undefined,
  required: string,
): boolean {
  const have = granted ?? [];
  if (have.includes("*")) return true;
  if (have.includes(required)) return true;
  const category = required.split(":")[0];
  return have.includes(`${category}:*`);
}

/**
 * The permissions the backend would treat the user as holding. `platform_admin`
 * is `*` by ROLE NAME, not by a stored permission row: `RBACService
 * ._get_user_permissions_in_session` returns `["*"]` for that role, and the seed
 * skips the wildcard ("checked via role name"), so /auth/login, /refresh and
 * /me can hand such a user `permissions: []` while the backend accepts every
 * call. Only that role name is a wildcard there (a role called `admin` is not),
 * so only it is here.
 */
export function effectivePermissions(
  role: string | null | undefined,
  permissions: readonly string[] | null | undefined,
): readonly string[] {
  if (role === "platform_admin") return ["*"];
  return permissions ?? [];
}

export interface ActionGate {
  allowed: boolean;
  /** True while the user's project rule role is not known yet (or could not
   * be read): closed, but not for a missing permission, so no notice lists it. */
  unknown?: boolean;
  /** The codename the action needs. */
  permission: string;
  /** Shown as the tooltip / inline text when not allowed; null when allowed. */
  reason: string | null;
}

export function requiresText(permission: string, projectRole?: string): string {
  return projectRole
    ? `Requires the ${permission} permission (your project rule role: ${projectRole})`
    : `Requires the ${permission} permission`;
}

/**
 * `projectRole` (the label of the user's role on the project, e.g. "DQ
 * Analyst" or "no rule role") is named in the reason when the gate comes from
 * the project's /access answer, so the user knows the fix is a project role.
 */
export function gateFor(
  granted: readonly string[] | null | undefined,
  action: RulesAction,
  projectRole?: string,
): ActionGate {
  const permission = RULES_ACTION_PERMISSION[action];
  const allowed = permissionGranted(granted, permission);
  return {
    allowed,
    permission,
    reason: allowed ? null : requiresText(permission, projectRole),
  };
}

export type RulesGates = Readonly<Record<RulesAction, ActionGate>>;

export function rulesGates(
  granted: readonly string[] | null | undefined,
  projectRole?: string,
): RulesGates {
  return {
    run: gateFor(granted, "run", projectRole),
    nl: gateFor(granted, "nl", projectRole),
    create: gateFor(granted, "create", projectRole),
    update: gateFor(granted, "update", projectRole),
    approve: gateFor(granted, "approve", projectRole),
    retire: gateFor(granted, "retire", projectRole),
    adopt: gateFor(granted, "adopt", projectRole),
  };
}

/** Every gate closed with one neutral reason: the project rule role is not known (yet). */
export function unknownRulesGates(reason: string): RulesGates {
  const closed = (action: RulesAction): ActionGate => ({
    allowed: false,
    unknown: true,
    permission: RULES_ACTION_PERMISSION[action],
    reason,
  });
  return {
    run: closed("run"),
    nl: closed("nl"),
    create: closed("create"),
    update: closed("update"),
    approve: closed("approve"),
    retire: closed("retire"),
    adopt: closed("adopt"),
  };
}

/** The distinct permissions the user lacks, in a stable order, for one summary notice. */
export function missingRulesPermissions(gates: RulesGates): string[] {
  const missing: string[] = [];
  for (const action of [
    "run",
    "create",
    "update",
    "approve",
    "retire",
  ] as const) {
    const gate = gates[action];
    if (!gate.allowed && !gate.unknown && !missing.includes(gate.permission)) {
      missing.push(gate.permission);
    }
  }
  return missing;
}
